import { KLAVIYO_TOKENS } from '@portal/config/base';
import {
  AGZ_PDP_EXPERIMENT_CONTROL_PDP,
  AGZ_PDP_EXPERIMENT_KLAVIYO_MODAL_PAGES
} from '@portal/constants/experiment-constants';
import { useExperiment } from '@statsig/react-bindings';
import { KLAVIYO_LOCATION, KLAVIYO_POPUP_TITLE } from 'analytics/constants';
import {
  emailSubmissionEvent,
  phoneSubmissionEvent,
  popupViewedEvent
} from 'analytics/events/studio';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useCallback, useEffect, useRef } from 'react';
import { SSFlags, SSParams } from 'state/context/StatsigContext/StatsigFlags';
import { useStoreValue } from 'state/hooks/useStore';

// Utility function to determine Klaviyo popupTitle based on popupText
function getPopupTitle(popupText: string): string {
  if (!popupText) return '';

  const match = KLAVIYO_POPUP_TITLE.find(({ keywords }) =>
    keywords.some((keyword) => popupText.includes(keyword))
  );

  return match ? match.value : '';
}

const KlaviyoScript = () => {
  const router = useRouter();
  const path = router.asPath;
  const store = useStoreValue();
  const observerRef = useRef<MutationObserver | null>(null);
  const companyId = KLAVIYO_TOKENS[store];

  // Get the experiment state to determine if user is in variant group
  // Disable exposure logging since the middleware already logs exposures
  const agzPdpExperiment = useExperiment(SSFlags.agzPdpExperiment, { disableExposureLog: true });
  const isVariant = agzPdpExperiment.get(SSParams[SSFlags.agzPdpExperiment].isVariant, false);

  useEffect(() => {
    let timeoutId: number | undefined;

    const handleKlaviyoForm = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const eventDetail = event.detail;
      const { metaData } = eventDetail || {};
      const isOpen = eventDetail?.type === 'open';
      const isSubmit = eventDetail?.type === 'submit';
      const isClose = eventDetail?.type === 'close';

      const a11yButton = document.getElementById('usntA40Toggle');

      // Handle open event / focus on a11y widget
      if (isOpen && a11yButton) {
        timeoutId = window.setTimeout(() => {
          a11yButton.setAttribute('tabindex', '0');
          a11yButton.focus();
        }, 250);
      }

      // Handle close event - track that user closed modal in this session
      if (isClose) {
        // Only track close for AGZ experiment pages
        if (AGZ_PDP_EXPERIMENT_KLAVIYO_MODAL_PAGES.includes(path)) {
          sessionStorage.setItem('klaviyo-agz-closed', 'true');
        }
      }

      // Use a mutation observer to scrape DOM content on initial form open
      if (isOpen) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              const formElement = document.querySelector('.klaviyo-form');
              if (formElement) {
                // Check Klaviyo form DOM content to detect modal type
                const ctaButton = formElement.querySelector('button:not([type="reset"])');
                const ctaText = ctaButton?.textContent || undefined;
                const popupText = formElement.textContent?.trim() || null;
                const popupTitle = popupText && getPopupTitle(popupText);

                popupViewedEvent({ clickText: ctaText, popupTitle, location: KLAVIYO_LOCATION });
                observer.disconnect();
              }
            }
          });
        });
        observerRef.current = observer;
        observerRef.current.observe(document.body, {
          childList: true,
          subtree: true
        });

        setTimeout(() => {
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }, 10000); // Disconnect observer after some time if data not found
      }

      // Fire event when popup form is submitted
      if (isSubmit) {
        // Track submission for AGZ experiment pages (persists across sessions)
        if (AGZ_PDP_EXPERIMENT_KLAVIYO_MODAL_PAGES.includes(path)) {
          localStorage.setItem('klaviyo-agz-submitted', 'true');
        }

        const formElement = document.querySelector('.klaviyo-form');

        // Get CTA text
        const ctaButton = formElement?.querySelector('button:not([type="reset"])');
        const ctaText = ctaButton?.textContent || undefined;

        // Checking Klaviyo text to detect modal's type
        const popupText = formElement?.textContent?.trim() || null;
        const popupTitle = popupText && getPopupTitle(popupText);

        const email = metaData?.$email || metaData?.email;
        const phone = metaData?.$phone_number || metaData?.phone_number;

        if (email) {
          emailSubmissionEvent({
            clickText: ctaText,
            popupTitle,
            email,
            location: KLAVIYO_LOCATION
          });
        }

        if (phone) {
          phoneSubmissionEvent({
            clickText: ctaText,
            popupTitle,
            phone,
            location: KLAVIYO_LOCATION
          });
        }
      }
    };

    window.addEventListener('klaviyoForms', handleKlaviyoForm);
    return () => {
      window.removeEventListener('klaviyoForms', handleKlaviyoForm);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [path]);

  const openKlaviyoAgzModal = useCallback(() => {
    // Don't fire modal if not on experiment pages
    if (!AGZ_PDP_EXPERIMENT_KLAVIYO_MODAL_PAGES.includes(path)) return;

    // Special case: If on control page, only show modal if user is in variant group
    // (because variant users get rewritten to the variant page but URL stays the same)
    if (path === AGZ_PDP_EXPERIMENT_CONTROL_PDP && !isVariant) return;

    // Check if user has already submitted (persists across sessions)
    const hasSubmitted = localStorage.getItem('klaviyo-agz-submitted') === 'true';
    if (hasSubmitted) return;

    // Check if user closed modal in current session (clears on new session)
    const hasClosed = sessionStorage.getItem('klaviyo-agz-closed') === 'true';
    if (hasClosed) return;

    // Detect if user is on mobile device
    const isMobile = window.innerWidth <= 768;

    // Use different form IDs based on device type
    const AGZ_KLAVIYO_FORM_ID = isMobile ? 'QZpnzQ' : 'VW9KXU';
    const AGZ_KLAVIYO_FORM_DELAY = 3000;

    const timeout = setTimeout(() => {
      if (window.klaviyo) window.klaviyo.openForm(AGZ_KLAVIYO_FORM_ID);
    }, AGZ_KLAVIYO_FORM_DELAY);
    return () => clearTimeout(timeout);
  }, [path, isVariant]);

  // Trigger AGZ modal on URL changes when Klaviyo is already loaded
  useEffect(() => {
    // Only call if Klaviyo is already loaded
    if (window.klaviyo) {
      openKlaviyoAgzModal();
    }
  }, [path, openKlaviyoAgzModal]);

  return (
    <>
      {companyId && (
        <Script
          src={`https://static.klaviyo.com/onsite/js/${companyId}/klaviyo.js`}
          onLoad={() => {
            openKlaviyoAgzModal();
          }}
        />
      )}
    </>
  );
};

export default KlaviyoScript;
